import Image from 'next/image';
import { useState } from 'react';
import { FeatureModalImage } from './features-image-modal';
import clsx from 'clsx';

type SingleFeatureImageProps = {
  image: FeatureModalImage;
  images?: never;
};

type MultipleFeatureImageProps = {
  image?: never;
  images: FeatureModalImage[];
};

type FeaturePreviewImageProps = (SingleFeatureImageProps | MultipleFeatureImageProps) & {
  imageClassName?: string;
  imageFramed?: boolean;
  onOpenImage: (image: FeatureModalImage) => void;
};

export function FeaturePreviewImage({
  image,
  images,
  imageClassName,
  imageFramed = true,
  onOpenImage,
}: FeaturePreviewImageProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  let previewImages: FeatureModalImage[] = [];

  if (images?.length) {
    previewImages = images;
  } else if (image) {
    previewImages = [image];
  }

  if (!previewImages.length) {
    return null;
  }

  if (previewImages.length === 1) {
    const [singleImage] = previewImages;

    return (
      <button
        aria-label={`Agrandir l'illustration ${singleImage.alt}`}
        type="button"
        className={clsx(
          'group h-full w-full text-left',
          imageFramed && 'overflow-hidden rounded-(--radius-current)',
        )}
        onClick={() => onOpenImage(singleImage)}
      >
        <Image
          alt={singleImage.alt}
          decoding="async"
          height={singleImage.height}
          loading="lazy"
          sizes="(min-width: 1024px) 50vw, 100vw"
          src={singleImage.src}
          width={singleImage.width}
          className={clsx(
            'w-full cursor-pointer',
            imageFramed && 'rounded-(--radius-current)',
            imageClassName,
          )}
        />
      </button>
    );
  }

  return (
    <div
      className={clsx(
        'flex h-full w-full items-stretch gap-2',
        imageFramed && 'overflow-hidden rounded-(--radius-current)',
      )}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {previewImages.map((previewImage, index) => {
        const isHovered = hoveredIndex === index;
        let flexGrow = 1;

        if (hoveredIndex !== null) {
          flexGrow = isHovered ? 1.8 : 0.9;
        }

        return (
          <button
            key={`${previewImage.src}-${index}`}
            aria-label={`Agrandir l'illustration ${previewImage.alt}`}
            className="min-w-[18%] basis-0 overflow-hidden transition-[flex-grow,transform] duration-300 ease-out"
            style={{ flexGrow }}
            type="button"
            onBlur={() => setHoveredIndex(null)}
            onClick={() => onOpenImage(previewImage)}
            onFocus={() => setHoveredIndex(index)}
            onMouseEnter={() => setHoveredIndex(index)}
          >
            <Image
              alt={previewImage.alt}
              decoding="async"
              height={previewImage.height}
              loading="lazy"
              sizes="(min-width: 1024px) 33vw, 100vw"
              src={previewImage.src}
              width={previewImage.width}
              className={clsx(
                'h-full w-full cursor-pointer object-cover transition-transform duration-300 ease-out',
                isHovered && 'scale-[1.02]',
                imageClassName,
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
